import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

interface NavBarProps {
  isHomepage?: boolean;
  activeSection?: string;
  onSectionClick?: (section: string) => void;
}

const NavBar: React.FC<NavBarProps> = ({ isHomepage = false, activeSection, onSectionClick }) => {
  const handleSectionClick = (section: string) => {
    if (onSectionClick) onSectionClick(section);
  };
  const { language, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Close mobile menu on outside click
  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const nav = document.querySelector('nav');
      if (nav && !nav.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  const prefix = isHomepage ? '#' : '/#';
  const blogHref = isHomepage ? '#blog' : '/blog';

  const navLinkClass = (section: string) =>
    `${activeSection === section ? 'text-gold' : 'text-slate'} hover:text-gold transition-colors duration-300 font-medium text-lg`;
  const mobileNavLinkClass = (section: string) =>
    `block ${activeSection === section ? 'text-gold' : 'text-slate'} hover:text-gold transition-colors font-medium`;

  const handleConsultationClick = () => {
    if (isHomepage) {
      const contactElement = document.getElementById('contact');
      if (contactElement) contactElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 border-b border-border-gray">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center h-20">
          {/* Left section: Logo */}
          <a
            href="/"
            aria-current={isHomepage && activeSection === 'home' ? 'location' : undefined}
            className="hover:opacity-90 transition-all duration-300 flex flex-col items-center flex-shrink-0 no-underline"
          >
            {language === 'bg' ? (
              <>
                <p className="text-xs text-gold font-bold tracking-[0.3em] uppercase mb-1">
                  {t('nav.navFirstLine')}
                </p>
                <span className="text-2xl font-serif font-bold text-navy leading-none tracking-wide">
                  {t('nav.navSecondLine')}
                </span>
              </>
            ) : (
              <>
                <span className="text-2xl font-serif font-bold text-navy leading-none tracking-wide mb-1">
                  {t('nav.navFirstLine')}
                </span>
                <p className="text-xs text-gold font-bold tracking-[0.3em] uppercase">
                  {t('nav.navSecondLine')}
                </p>
              </>
            )}
          </a>

          {/* Middle section: Nav links + Language + Consultation - centered with flex-1 */}
          <div className={`hidden lg:flex flex-1 items-center justify-center ${language === 'en' ? 'space-x-6' : 'space-x-4'}`}>
            <a href={`${prefix}about`} onClick={() => handleSectionClick('about')} aria-current={activeSection === 'about' ? 'location' : undefined} className={navLinkClass('about')}>{t('nav.about')}</a>
            <a href={`${prefix}awards`} onClick={() => handleSectionClick('awards')} aria-current={activeSection === 'awards' ? 'location' : undefined} className={navLinkClass('awards')}>{t('nav.awards')}</a>
            <a href={`${prefix}practice`} onClick={() => handleSectionClick('practice')} aria-current={activeSection === 'practice' ? 'location' : undefined} className={navLinkClass('practice')}>{t('nav.practice')}</a>
            <a href={`${prefix}notable-cases`} onClick={() => handleSectionClick('notable-cases')} aria-current={activeSection === 'notable-cases' ? 'location' : undefined} className={navLinkClass('notable-cases')}>{t('nav.notableCases')}</a>
            <a href={`${prefix}faq`} onClick={() => handleSectionClick('faq')} aria-current={activeSection === 'faq' ? 'location' : undefined} className={navLinkClass('faq')}>{t('nav.faq')}</a>
            <a href={blogHref} onClick={() => handleSectionClick('blog')} aria-current={activeSection === 'blog' ? 'location' : undefined} className={navLinkClass('blog')}>{t('nav.insights')}</a>
            <a href={`${prefix}contact`} onClick={() => handleSectionClick('contact')} aria-current={activeSection === 'contact' ? 'location' : undefined} className={navLinkClass('contact')}>{t('nav.contact')}</a>
            <LanguageSwitcher />
            <button onClick={handleConsultationClick} className="btn-primary text-sm px-6 py-2">{t('nav.consultation')}</button>
          </div>

          {/* Mobile controls: Language + Hamburger */}
          <div className="flex lg:hidden items-center space-x-3 ml-auto">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-navy focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-3">
            <a href={`${prefix}about`} onClick={() => { handleSectionClick('about'); setMobileMenuOpen(false); }} aria-current={activeSection === 'about' ? 'location' : undefined} className={mobileNavLinkClass('about')}>{t('nav.about')}</a>
            <a href={`${prefix}awards`} onClick={() => { handleSectionClick('awards'); setMobileMenuOpen(false); }} aria-current={activeSection === 'awards' ? 'location' : undefined} className={mobileNavLinkClass('awards')}>{t('nav.awards')}</a>
            <a href={`${prefix}practice`} onClick={() => { handleSectionClick('practice'); setMobileMenuOpen(false); }} aria-current={activeSection === 'practice' ? 'location' : undefined} className={mobileNavLinkClass('practice')}>{t('nav.practice')}</a>
            <a href={`${prefix}notable-cases`} onClick={() => { handleSectionClick('notable-cases'); setMobileMenuOpen(false); }} aria-current={activeSection === 'notable-cases' ? 'location' : undefined} className={mobileNavLinkClass('notable-cases')}>{t('nav.notableCases')}</a>
            <a href={`${prefix}faq`} onClick={() => { handleSectionClick('faq'); setMobileMenuOpen(false); }} aria-current={activeSection === 'faq' ? 'location' : undefined} className={mobileNavLinkClass('faq')}>{t('nav.faq')}</a>
            <a href={blogHref} onClick={() => { handleSectionClick('blog'); setMobileMenuOpen(false); }} aria-current={activeSection === 'blog' ? 'location' : undefined} className={mobileNavLinkClass('blog')}>{t('nav.insights')}</a>
            <a href={`${prefix}contact`} onClick={() => { handleSectionClick('contact'); setMobileMenuOpen(false); }} aria-current={activeSection === 'contact' ? 'location' : undefined} className={mobileNavLinkClass('contact')}>{t('nav.contact')}</a>
            <div className="pt-2 border-t border-gray-100">
              <button onClick={() => { setMobileMenuOpen(false); handleConsultationClick(); }} className="btn-primary text-sm px-4 py-2 w-full">{t('nav.consultation')}</button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
